import { getRouteApi } from '@tanstack/react-router';
import type { PermissionsResponseDTO } from '@tempo-io/id-dto';
import type { Permission } from '@tempo-io/permissions';
import { HTTPError } from 'ky';
import { TempoApiError } from '@/common/api/tempoApiTypes';

const routeApi = getRouteApi('/(app)');

export const usePermissions = <K extends Permission>(
  permissions: readonly K[],
): Record<K, boolean> => {
  const routeContext = routeApi.useRouteContext();
  const map = routeContext.permissions;
  return Object.fromEntries(
    permissions.map((permission) => [permission, map[permission] ?? false]),
  ) as Record<K, boolean>;
};

export const PERMISSION_DENIED = 'Permission Denied';

export class PermissionDeniedError extends Error {
  readonly permission: Permission;

  constructor(permission: Permission) {
    super(PERMISSION_DENIED);
    this.name = 'PermissionDeniedError';
    this.permission = permission;
  }
}

const PERMISSION_DENIED_STATUS = 403;

export const isPermissionDeniedError = (error: unknown): boolean => {
  if (error instanceof TempoApiError) {
    return error.error.status === PERMISSION_DENIED_STATUS;
  }
  if (error instanceof HTTPError) {
    return error.response.status === PERMISSION_DENIED_STATUS;
  }
  if (error instanceof Error) {
    return error.message === PERMISSION_DENIED;
  }
  return false;
};

export const requirePermission =
  (permission: Permission) =>
  async ({ context }: { context: { permissions: Partial<PermissionsResponseDTO> } }) => {
    if (!context.permissions[permission]) {
      throw new PermissionDeniedError(permission);
    }
  };
